<script lang="ts" setup>
import { useDefaultLookupFieldValues } from '@/components/form-builder/field-settings/useDefaultLookupFieldValues'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'

interface Props {
  field: {
    lookupEntity: {
      entity: string
    }
    defaultValue?: string | number | null
    uniqueKey: () => string
  }
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (
    e: 'modify-field',
    payload: {
      propertyKey: string
      fieldValue: any
      key: string
    },
  ): void
}>()

const {
  showDefaultValue,
  toggleDefaultValue,
  lookupTypeChoices,
  currentLookupTypeName,
  currentOptions,
  currentDisplayProps,
  isLocationEntity,
  locationValue,
  onDefaultValueChange,
  changeLookupType,
} = useDefaultLookupFieldValues(props.field)

function handleLocationChange(value: number | number[] | undefined) {
  onDefaultValueChange(value as number | null)
  emitChanges('defaultValue', props.field.defaultValue)
}

function emitChanges(propertyKey: string, value: any) {
  emit('modify-field', {
    propertyKey,
    fieldValue: value,
    key: props.field.uniqueKey(),
  })
}
</script>

<template>
  <vs-wrapper space="small">
    <v-radio-group
      :value="field.lookupEntity.entity"
      label="Choose the lookup selector type"
      mandatory
      @change="(value) => changeLookupType(value, emitChanges)"
    >
      <v-radio
        v-for="choice in lookupTypeChoices"
        :key="choice.value"
        :label="choice.name"
        :value="choice.value"
      >
      </v-radio>
    </v-radio-group>

    <v-checkbox
      :input-value="showDefaultValue"
      label="Default Value"
      @change="toggleDefaultValue"
    />

    <div v-if="showDefaultValue">
      <div v-if="isLocationEntity">
        <new-location-picker
          :value="locationValue"
          label="Search for default location"
          placeholder="Let the dispatcher decide location"
          @input="handleLocationChange"
        />
      </div>
      <v-select
        v-else-if="!isLocationEntity"
        v-model="field.defaultValue"
        :item-text="currentDisplayProps.textKey"
        :item-value="currentDisplayProps.valueKey"
        :items="currentOptions"
        :label="`Default ${currentLookupTypeName}`"
        :placeholder="`Select default ${currentLookupTypeName.toLowerCase()}`"
        clearable
        @input="onDefaultValueChange"
      />
      <vs-text-input
        v-else
        v-model="field.defaultValue"
        :placeholder="`Enter default ${currentLookupTypeName.toLowerCase()}`"
        label=""
      />
    </div>
  </vs-wrapper>
</template>

<style scoped>
.location-container {
  border: 1px solid var(--color-grey--light);
  border-radius: 4px;
  padding: 12px;
  max-height: 400px;
  overflow: hidden;
}

.location-container :deep(.location-filter) {
  max-height: 350px;
}
</style>
